import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type {
  MedicalReservationFilterOptions,
  ActionStatus,
} from '../../types/medicalReservation';
import { ACTION_STATUS_CONFIG } from '../../types/medicalReservation';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: MedicalReservationFilterOptions;
  onFiltersChange: (filters: MedicalReservationFilterOptions) => void;
  doctorOptions?: string[];
  uploadBatchOptions?: Array<{ id: string; name: string; uploaded_at: string }>;
  presentation?: 'drawer' | 'inline';
  applyRef?: React.MutableRefObject<(() => void) | null>;
}

/**
 * 診療予約フィルター・検索ドロワー
 * 
 * 意図：
 * - 詳細な検索・フィルタリング機能
 * - 直感的なUI操作
 * - リアルタイム検索結果反映
 * 
 * 注意点：
 * - パフォーマンス（デバウンス処理）
 * - フィルター状態の永続化
 * - レスポンシブ対応
 * 
 * 改善提案：
 * - 保存済みフィルター機能
 * - 高度な日付フィルター
 * - 自動補完機能
 */
export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  doctorOptions = [],
  uploadBatchOptions = [],
  presentation = 'drawer',
  applyRef,
}) => {
  const theme = useTheme();
  const [localFilters, setLocalFilters] = useState<MedicalReservationFilterOptions>(filters);
  const changedFields = useRef(new Set<keyof MedicalReservationFilterOptions>());
  const wasOpen = useRef(open);

  useEffect(() => {
    if (wasOpen.current && !open) {
      changedFields.current.clear();
      setLocalFilters(filters);
    }
    wasOpen.current = open;
  }, [open, filters]);

  useEffect(() => {
    setLocalFilters((current) => {
      const next = { ...filters };
      changedFields.current.forEach((field) => {
        Object.assign(next, { [field]: current[field] });
      });
      return next;
    });
  }, [filters]);

  const handleFilterChange = (field: keyof MedicalReservationFilterOptions, value: any) => {
    changedFields.current.add(field);
    const newFilters = {
      ...localFilters,
      [field]: value,
    };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = useCallback(() => {
    const nextFilters = { ...filters, page: 1 };
    changedFields.current.forEach((field) => {
      Object.assign(nextFilters, { [field]: localFilters[field] });
    });
    changedFields.current.clear();
    onFiltersChange(nextFilters);
    onClose();
  }, [filters, localFilters, onFiltersChange, onClose]);

  useEffect(() => {
    if (!applyRef) return;
    applyRef.current = handleApplyFilters;
    return () => { if (applyRef.current === handleApplyFilters) applyRef.current = null; };
  }, [applyRef, handleApplyFilters]);

  const handleClearFilters = () => {
    const clearedFilters: MedicalReservationFilterOptions = {
      ...filters,
      search: '',
      doctor: '',
      actionStatus: [],
      dateRange: undefined,
      uploadBatchId: '',
      page: 1,
      page_size: filters.page_size || 10,
      sort_by: 'consultationStartTime',
      sort_order: 'desc',
    };
    changedFields.current.clear();
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleActionStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    handleFilterChange(
      'actionStatus',
      typeof value === 'string' ? value.split(',') as ActionStatus[] : value as ActionStatus[]
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.doctor) count++;
    if (localFilters.actionStatus?.length) count++;
    if (localFilters.dateRange?.start || localFilters.dateRange?.end) count++;
    if (localFilters.uploadBatchId) count++;
    return count;
  };

  const content = (
      <Box className="reservation-filter-content" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <FilterIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              詳細絞り込み
            </Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={getActiveFilterCount()}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="reservation-filter-body" sx={{ flex: 1, overflow: 'auto' }}>
          <Stack className="reservation-filter-fields" spacing={3}>
            {presentation === 'drawer' && <TextField
              fullWidth
              label="検索"
              placeholder="anybot ID、患者名、担当医師で検索"
              value={localFilters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />}

            {presentation === 'drawer' && <Divider />}

            <FormControl fullWidth>
              <InputLabel>担当医師</InputLabel>
              <Select
                value={localFilters.doctor || ''}
                label="担当医師"
                onChange={(e) => handleFilterChange('doctor', e.target.value)}
              >
                <MenuItem value="">すべて</MenuItem>
                {doctorOptions.map((doctor) => (
                  <MenuItem key={doctor} value={doctor}>
                    {doctor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>通知状態（複数選択可）</InputLabel>
              <Select
                multiple
                value={localFilters.actionStatus || []}
                onChange={handleActionStatusChange}
                input={<OutlinedInput label="通知状態（複数選択可）" />}
                renderValue={(selected) =>
                  (selected as ActionStatus[]).map((value) => ACTION_STATUS_CONFIG[value].label).join('、')
                }
              >
                {Object.entries(ACTION_STATUS_CONFIG).map(([status, config]) => (
                  <MenuItem key={status} value={status}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                診療開始時間
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="開始日"
                  value={localFilters.dateRange?.start || ''}
                  onChange={(e) => {
                    handleFilterChange('dateRange', {
                      ...localFilters.dateRange,
                      start: e.target.value,
                    });
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="終了日"
                  value={localFilters.dateRange?.end || ''}
                  onChange={(e) => {
                    handleFilterChange('dateRange', {
                      ...localFilters.dateRange,
                      end: e.target.value,
                    });
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Box>

            {uploadBatchOptions.length > 0 && (
              <>
                <Divider />
                <FormControl fullWidth>
                  <InputLabel>アップロードバッチ</InputLabel>
                  <Select
                    value={localFilters.uploadBatchId || ''}
                    label="アップロードバッチ"
                    onChange={(e) => handleFilterChange('uploadBatchId', e.target.value)}
                  >
                    <MenuItem value="">すべて</MenuItem>
                    {uploadBatchOptions.map((batch) => (
                      <MenuItem key={batch.id} value={batch.id}>
                        {batch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </Box>

        {presentation === 'inline' ? (
          <Box className="reservation-filter-actions">
            <Typography className="reservation-filter-hint">条件を選び、上の「検索」で適用します</Typography>
            {getActiveFilterCount() > 0 && <Button variant="text" onClick={handleClearFilters}>条件をクリア</Button>}
          </Box>
        ) : (
          <Box className="reservation-filter-actions" sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Stack spacing={2}>
              <Button fullWidth variant="contained" onClick={handleApplyFilters} startIcon={<FilterIcon />}>この条件で検索</Button>
              <Button fullWidth variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>条件をクリア</Button>
            </Stack>
          </Box>
        )}
      </Box>
  );

  return presentation === 'inline' ? (
    <Box className="reservation-inline-filter" id="reservation-detail-filters" hidden={!open}>{content}</Box>
  ) : (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', sm: 400 }, maxWidth: '100vw' } }}>
      {content}
    </Drawer>
  );
};
